import {Component, ContentChild, EventEmitter, Input, Output, TemplateRef} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {ContextMenu} from 'primeng/contextmenu';
import {RefundSearchResultView} from 'web-angular-client';
import {getRefundStatusClass, getRefundStatusLabel} from '../../../sdk/interface/refundStatusUtil';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {RowSortValueAccessor} from '../../../sdk/util/row-sort';
import {SortableTableBase, SortDropdownOption} from '../../../sdk/component/table/sortable-table-base';

/**
 * Presentational table for refund search results, shared by the refund dashboard's recent refunds card and the refund
 * transactions page. It renders what it is handed and nothing more: the parent owns the rows, the loading and error
 * flags, and the per-row menu items.
 *
 * <p>
 * {@link SortableTableBase} owns both sort entry points — the column headers, and the "Sort By" dropdown that
 * replaces them at 1024px and below — and decides which of the two sort modes applies. The transactions page leaves
 * {@code clientSideSort} off, so a header click emits and the page re-queries; the dashboard card turns it on, so the
 * card reorders the few rows it holds instead of becoming "the largest refunds ever". Only the refund-native columns
 * can be sorted ({@link #SORTABLE_COLUMNS}) — the enriched account columns (account number, name, reference, store)
 * are joined in after the query, so the server cannot order by them.
 *
 * <p>
 * The body renders exactly one of four states, in precedence order: loading, error, the rows, then {@code emptyText}.
 * {@code showActions} adds the trailing row-actions column, which the placeholder rows' colspan accounts for. A parent
 * may project an {@code #expandedRow} template to add a detail row beneath any row it reports as expanded.
 */
@Component({
    selector: 'app-refund-table',
    templateUrl: './refund-table.component.html',
    standalone: false
})
export class RefundTableComponent extends SortableTableBase<RefundSearchResultView>
{
    @Input() refunds: RefundSearchResultView[] | null | undefined = [];
    @Input() loading = false;
    @Input() error = false;
    @Input() emptyText = 'No refunds for the selected filters.';
    @Input() loadingText = 'Loading…';
    @Input() errorText = 'Data unavailable';
    @Input() showActions = false;
    @Input() menuItemsForRefund: (row: RefundSearchResultView) => MenuItem[] = () => [];
    @Input() clickable = false;
    @Input() isExpanded: (row: RefundSearchResultView) => boolean = () => false;
    @Output() readonly refundSelect = new EventEmitter<RefundSearchResultView>();
    @Output() readonly rowClick = new EventEmitter<RefundSearchResultView>();

    @ContentChild('expandedRow', {static: false}) expandedRowTemplate: TemplateRef<{
        $implicit: RefundSearchResultView
    }> | null = null;

    activeMenuItems: MenuItem[] = [];

    /**
     * Sort value per column, for {@code clientSideSort} only. Dates are ISO strings, which compare correctly as text.
     */
    protected readonly sortValues: Record<string, RowSortValueAccessor<RefundSearchResultView>> = {
        dateTimeCreated: row => row?.refund?.dateTimeCreated,
        amount: row => row?.refund?.amount,
        completedAt: row => row?.refund?.completedAt
    };

    private static readonly SORTABLE_COLUMNS: ReadonlyArray<SortDropdownOption> = [
        {id: 'dateTimeCreated', name: 'Date Created'},
        {id: 'amount', name: 'Amount'},
        {id: 'completedAt', name: 'Date Completed'}
    ];

    private activeContextMenu: ContextMenu | null = null;

    constructor(private readonly authenticationService: AuthenticationService)
    {
        super();
    }

    protected get rows(): RefundSearchResultView[] | null | undefined
    {
        return this.refunds;
    }

    protected get sortableColumnOptions(): ReadonlyArray<SortDropdownOption>
    {
        return RefundTableComponent.SORTABLE_COLUMNS;
    }

    onRowClick(row: RefundSearchResultView): void
    {
        if (!this.clickable)
        {
            return;
        }
        this.rowClick.emit(row);
    }

    openMenu(event: MouseEvent, contextMenu: ContextMenu, row: RefundSearchResultView): void
    {
        event.stopPropagation();
        event.preventDefault();
        this.activeContextMenu?.hide();
        this.activeContextMenu = null;

        if (this.authenticationService.isGuestAdminUser())
        {
            return;
        }

        this.activeMenuItems = this.menuItemsForRefund(row);
        this.refundSelect.emit(row);
        contextMenu.show(event);
        this.activeContextMenu = contextMenu;
    }

    isGuestAdminUser(): boolean
    {
        return this.authenticationService.isGuestAdminUser();
    }

    statusLabel(row: RefundSearchResultView): string
    {
        return getRefundStatusLabel(row?.refund?.refundState);
    }

    statusClass(row: RefundSearchResultView): string
    {
        return getRefundStatusClass(row?.refund?.refundState);
    }
}
